import { ChevronDownIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCityMap } from "../../hooks/use-city-map";
import { useCreateServiceAccount, useUpdateServiceAccount } from "../../hooks/use-service-accounts";
import {
  CreateServiceAccountFormValues,
  UpdateServiceAccountFormValues,
  createServiceAccountSchema,
  defaultServiceAccount,
  updateServiceAccountSchema,
} from "../../service-accounts.const";
import { ServiceAccount } from "../../service-accounts.type";

interface ServiceAccountModalProps {
  serviceAccount?: ServiceAccount | null;
  onClose: () => void;
  onApiKeyGenerated?: (apiKey: string) => void;
}

export const ServiceAccountModal = ({ serviceAccount, onClose, onApiKeyGenerated }: ServiceAccountModalProps) => {
  const isUpdate = !!serviceAccount?.id;
  const [citiesOpen, setCitiesOpen] = useState(false);
  const [sortCitiesAZ, setSortCitiesAZ] = useState(true);

  const { createServiceAccount, loading: createLoading, error: createError } = useCreateServiceAccount();
  const {
    updateServiceAccount,
    loading: updateLoading,
    error: updateError,
  } = useUpdateServiceAccount(serviceAccount?.id ?? "");
  const { cityList, loading: citiesLoading } = useCityMap();

  const sortedCityList = useMemo(() => {
    return [...cityList].sort((a, b) => {
      return sortCitiesAZ
        ? a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        : b.label.localeCompare(a.label, undefined, { sensitivity: "base" });
    });
  }, [cityList, sortCitiesAZ]);

  const form = useForm<CreateServiceAccountFormValues | UpdateServiceAccountFormValues>({
    resolver: zodResolver(isUpdate ? updateServiceAccountSchema : createServiceAccountSchema),
    defaultValues: serviceAccount
      ? { name: serviceAccount.name, email: serviceAccount.email, cities: serviceAccount.cities }
      : defaultServiceAccount,
    mode: "all",
  });

  const saving = createLoading || updateLoading;

  const handleSave = async (data: CreateServiceAccountFormValues | UpdateServiceAccountFormValues) => {
    try {
      if (isUpdate) {
        await updateServiceAccount({ name: data.name, email: data.email, cities: data.cities });
        toast.success("Compte de service mis à jour avec succès", { style: { color: "#228B22" } });
      } else {
        const result = await createServiceAccount({ name: data.name, email: data.email, cities: data.cities });
        toast.success("Compte de service créé avec succès", { style: { color: "#228B22" } });
        if (result?.apiKey) {
          onApiKeyGenerated?.(result.apiKey);
        }
      }
      onClose();
      form.reset();
    } catch {
      const error = createError || updateError;
      toast.error(error?.message || "Une erreur s'est produite");
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-106.25">
        <DialogHeader>
          <DialogTitle>{isUpdate ? "Modifier" : "Créer"} un compte de service</DialogTitle>
          <DialogDescription>Informations du compte de service</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" disabled={isUpdate} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cities"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Villes</FormLabel>
                  <DropdownMenu open={citiesOpen} onOpenChange={setCitiesOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="default"
                        role="combobox"
                        aria-expanded={citiesOpen}
                        className={cn("w-full justify-between", !field.value?.length && "text-muted-foreground")}
                      >
                        <span>
                          {Array.isArray(field.value) && field.value.length > 0
                            ? `${field.value.length} villes sélectionnées`
                            : "Sélectionner les villes"}
                        </span>
                        <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-50 max-h-75 overflow-y-auto" align="start">
                      <div className="flex items-center justify-end px-2 py-1">
                        <Button
                          variant="outline"
                          type="button"
                          className="ml-auto text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-7 flex items-center"
                          onClick={() => setSortCitiesAZ((prev) => !prev)}
                        >
                          <span className="hidden lg:inline">{sortCitiesAZ ? "Sort (Z-A)" : "Sort (A-Z)"}</span>
                        </Button>
                      </div>
                      {citiesLoading ? (
                        <Skeleton />
                      ) : (
                        sortedCityList.map((city) => (
                          <DropdownMenuItem
                            key={city.value}
                            onSelect={(e) => {
                              e.preventDefault();
                              const values = field.value || [];
                              const newValues = values.includes(city.value)
                                ? values.filter((v) => v !== city.value)
                                : [...values, city.value];
                              field.onChange(newValues);
                            }}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox checked={field.value?.includes(city.value)} className="mr-2" />
                            <span>{city.label}</span>
                          </DropdownMenuItem>
                        ))
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onClose();
                  form.reset();
                }}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className={`bg-primary text-primary-foreground hover:bg-primary/90 ${saving && "cursor-not-allowed opacity-55"}`}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    En cours...
                  </>
                ) : isUpdate ? (
                  "Mettre à jour"
                ) : (
                  "Créer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
